import React, { useState, useEffect, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import TextField from "@material-ui/core/TextField";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import Shell from "./Shell";
import { useAdminStyles, brl, num, since, date } from "./styles";

// Recortes de "quem está usando". São estados de ciclo de vida, não filtros
// genéricos — cada um responde a uma pergunta de negócio diferente.
const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "active", label: "Usando (30d)", hint: "executou algum fluxo nos últimos 30 dias" },
  { key: "building", label: "Construindo", hint: "criou fluxo mas nunca executou" },
  { key: "churning", label: "Pararam", hint: "já executou, nada nos últimos 30 dias" },
  { key: "empty", label: "Vazios", hint: "cadastrou e não criou nada" },
  { key: "paying", label: "Pagantes", hint: "assinatura em plano pago" },
  { key: "blocked", label: "Bloqueados", hint: "conta suspensa pela equipe" },
];

const COLS_BASE = [
  { key: "name", label: "Usuário", sortable: true },
  { key: null, label: "Plano" },
  { key: null, label: "Fluxos", num: true },
  { key: null, label: "Agentes", num: true },
  { key: null, label: "Nº / Conect.", num: true },
  { key: "runs", label: "Execuções 30d", num: true, sortable: true },
  { key: null, label: "Erros", num: true },
  { key: "spend", label: "Consumo 30d", num: true, sortable: true, dinheiro: true },
  { key: "recent", label: "Último acesso", sortable: true },
];

const Users = () => {
  const classes = useAdminStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const podeVerDinheiro = !!(user && user.canSeeMoney);
  const COLS = COLS_BASE.filter(c => !c.dinheiro || podeVerDinheiro);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("runs");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/usage-summary").then(r => setSummary(r.data)).catch(toastError);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users", { params: { filter, sort, search, limit: 200 } });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      toastError(err);
    }
  }, [filter, sort, search]);

  // busca com respiro: sem isso é uma requisição por tecla digitada
  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const b = summary && summary.buckets;
  const e = summary && summary.estagios;

  return (
    <Shell title="Quem está usando">
      {summary && (
        <>
          <div className={classes.cards}>
            <div className={classes.kpi}>
              <div className={classes.kpiLabel}>Usando (30d)</div>
              <div className={classes.kpiValue}>{num(e.ativos)}</div>
              <div className={classes.kpiHint}>executaram algum fluxo</div>
            </div>
            <div className={classes.kpi}>
              <div className={classes.kpiLabel}>Construindo</div>
              <div className={classes.kpiValue}>{num(e.construindo)}</div>
              <div className={classes.kpiHint}>criaram fluxo, nunca rodaram</div>
            </div>
            <div className={classes.kpi}>
              <div className={classes.kpiLabel}>Pararam</div>
              <div className={classes.kpiValue}>{num(e.pararam)}</div>
              <div className={classes.kpiHint}>rodaram antes, nada em 30d</div>
            </div>
            <div className={classes.kpi}>
              <div className={classes.kpiLabel}>Vazios</div>
              <div className={classes.kpiValue}>{num(e.vazios)}</div>
              <div className={classes.kpiHint}>cadastro sem nenhum fluxo</div>
            </div>
          </div>

          <div className={classes.cols}>
            <div className={classes.card}>
              <div className={classes.cardTitle}>Intensidade de uso (30 dias)</div>
              <div className={classes.cardSub}>Quantos clientes em cada faixa de execuções.</div>
              {[
                ["Sem uso", b.semUso],
                ["1 a 10", b.ate10],
                ["11 a 100", b.ate100],
                ["101 a 1.000", b.ate1000],
                ["Acima de 1.000", b.acima1000],
              ].map(([label, v], i) => (
                <div key={label} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
                  <div className={classes.grow}><div className={classes.strong}>{label}</div></div>
                  <div className={classes.num}>{num(v)}</div>
                </div>
              ))}
            </div>

            <div className={classes.card}>
              <div className={classes.cardTitle}>Recursos adotados</div>
              <div className={classes.cardSub}>Quantos clientes usam cada tipo de recurso.</div>
              {[
                ["Agentes", summary.recursos.agentes],
                ["Gatilho webhook", summary.recursos.webhooks],
                ["Chamada recebida", summary.recursos.chamadas],
                ["Agendamento", summary.recursos.agendados],
                ["Ferramentas (MCP)", summary.recursos.ferramentas],
                ["Números", summary.recursos.numeros],
                ["Conectores", summary.recursos.conectores],
                ["Chaves de API", summary.recursos.apiKeys],
              ].map(([label, v], i) => (
                <div key={label} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
                  <div className={classes.grow}><div className={classes.strong}>{label}</div></div>
                  <div className={classes.num}>{num(v)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className={classes.card}>
        <div className={classes.filters}>
          {FILTERS.map(f => (
            <div
              key={f.key}
              title={f.hint || ""}
              className={`${classes.chip} ${filter === f.key ? classes.chipOn : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </div>
          ))}
          <div className={classes.grow} />
          <TextField
            placeholder="Buscar por nome ou e-mail"
            size="small"
            variant="outlined"
            value={search}
            onChange={ev => setSearch(ev.target.value)}
            style={{ minWidth: 240 }}
          />
        </div>

        <div className={classes.cardSub}>{num(total)} usuário(s) neste recorte</div>

        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                {COLS.map(c => (
                  <th
                    key={c.label}
                    className={`${classes.th} ${c.sortable ? "" : classes.thPlain}`}
                    style={c.num ? { textAlign: "right" } : undefined}
                    onClick={c.sortable ? () => setSort(c.key) : undefined}
                  >
                    {c.label}{sort === c.key ? " ↓" : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className={classes.trLink} onClick={() => history.push(`/admin/users/${r.id}`)}>
                  <td className={classes.td}>
                    <div className={classes.strong}>
                      {r.name || "—"}
                      {r.blockedAt ? <span className={`${classes.pill} ${classes.bad}`} style={{ marginLeft: 8 }}>Bloqueado</span> : null}
                    </div>
                    <div className={classes.muted}>{r.email} · desde {date(r.createdAt)}</div>
                  </td>
                  <td className={classes.td}>
                    <span className={`${classes.pill} ${r.subStatus === "active" ? classes.ok : r.subStatus === "past_due" ? classes.bad : classes.neutral}`}>
                      {r.planName || "—"}
                    </span>
                  </td>
                  <td className={`${classes.td} ${classes.tdNum}`}>
                    {num(r.apps)}<span className={classes.muted}> / {num(r.published)}</span>
                  </td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{num(r.agents)}</td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{num(r.numbers)} / {num(r.connectors)}</td>
                  <td className={`${classes.td} ${classes.tdNum}`}><b>{num(r.runs30)}</b></td>
                  <td className={`${classes.td} ${classes.tdNum}`}>
                    {r.errors30 ? <span className={`${classes.pill} ${classes.bad}`}>{num(r.errors30)}</span> : "—"}
                  </td>
                  {podeVerDinheiro && (
                    <td className={`${classes.td} ${classes.tdNum}`}>{brl(r.spend30Cents)}</td>
                  )}
                  <td className={classes.td}>
                    <div>{since(r.lastSeenAt)}</div>
                    <div className={classes.muted}>execução {since(r.lastRunAt)}</div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td className={classes.td} colSpan={COLS.length}>
                  <div className={classes.empty}>Nenhum usuário neste recorte.</div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
};

export default Users;
